import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { Key, Plus, Clock } from 'lucide-react';
import { api } from '@/lib/api-client';
import { CreateOAuthClientDrawer } from '@/components/oauth/CreateOAuthClientDrawer';
import { OAuthClientDetailDrawer, type OAuthClientItem } from '@/components/oauth/OAuthClientDetailDrawer';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { listControlsGlassTrigger } from '@/components/capability-list/list-controls-classes';
import { cn } from '@/lib/utils';

const createButtonClass =
    'inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2';

export default function ApplicationOAuthClientsPage() {
    const { appKey } = useParams<{ appKey: string }>();
    usePageTitle(`API Access — ${appKey}`);
    const { setHeaderAction, setHeaderTitle } = useHeaderAction();
    const [createOpen, setCreateOpen] = useState(false);
    const [clients, setClients] = useState<OAuthClientItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState<OAuthClientItem | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const apiBasePath = `/applications/${appKey}/oauth-clients`;

    const fetchClients = async () => {
        try {
            const { data } = await api.get<{ oauth_clients: OAuthClientItem[] }>(apiBasePath);
            setClients(data.oauth_clients ?? []);
        } catch {
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, [appKey]);

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
                    <Key size={16} />
                </div>
            ),
            title: 'API Access',
            subtitle: 'Manage OAuth 2.0 client credentials for this application.',
        });
        setHeaderAction(
            <button type="button" onClick={() => setCreateOpen(true)} className={createButtonClass}>
                <Plus size={14} />
                Create Client
            </button>,
        );
        return () => {
            setHeaderTitle(null);
            setHeaderAction(null);
        };
    }, [setHeaderAction, setHeaderTitle]);

    const handleCreated = () => {
        setCreateOpen(false);
        fetchClients();
    };

    const handleUpdated = () => {
        setSelectedClient(null);
        fetchClients();
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const formatDate = (iso: string) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
        } catch {
            return iso;
        }
    };

    const filteredClients = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        let result = clients.filter((c) => {
            const matchesSearch =
                !q ||
                c.name.toLowerCase().includes(q) ||
                c.client_id.toLowerCase().includes(q) ||
                c.description.toLowerCase().includes(q);
            const matchesStatus = statusFilter === 'all' || c.status.toLowerCase() === statusFilter;
            return matchesSearch && matchesStatus;
        });

        return [...result].sort((a, b) => {
            let cmp = 0;
            if (sortField === 'created') {
                cmp = a.created.localeCompare(b.created);
            } else if (sortField === 'status') {
                cmp = a.status.localeCompare(b.status);
            } else {
                cmp = a.name.localeCompare(b.name);
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });
    }, [clients, searchQuery, statusFilter, sortField, sortDirection]);

    const statusBadge = (status: string) => {
        const map: Record<string, string> = {
            Active: 'bg-green-100 text-green-700',
            Disabled: 'bg-yellow-100 text-yellow-700',
            Revoked: 'bg-red-100 text-red-700',
        };
        return (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${map[status] ?? 'bg-slate-100 text-slate-700'}`}>
                {status}
            </span>
        );
    };

    const statusSelect = (
        <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className={cn('w-36', listControlsGlassTrigger)}>
                <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
            </SelectContent>
        </Select>
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <CapabilityListPanel<OAuthClientItem>
                items={filteredClients}
                sortOptions={[
                    { key: 'name', label: 'Name' },
                    { key: 'created', label: 'Created' },
                    { key: 'status', label: 'Status' },
                ]}
                sortField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                searchPlaceholder="Search OAuth clients..."
                allTags={[]}
                selectedTags={new Set<string>()}
                onToggleTag={() => {}}
                onClearAllTags={() => {}}
                onSelectAllTags={() => {}}
                showTagsFilter={false}
                extraToolbar={statusSelect}
                isLoading={loading}
                loadingMessage="Loading OAuth clients..."
                emptyStateVariant="glass"
                emptyMessage="No OAuth clients for this application. Create one to enable API access."
                renderCard={(c) => (
                    <div
                        key={c.id}
                        onClick={() => setSelectedClient(c)}
                        className="relative z-0 flex min-h-0 w-full cursor-pointer flex-col rounded-lg border border-slate-200 border-l-4 border-l-indigo-500 bg-white p-3 shadow-sm transition-shadow duration-150 hover:border-slate-300 hover:bg-slate-50 hover:shadow-none"
                    >
                        <div className="flex min-h-[88px] flex-row items-start gap-2 pr-2">
                            <div className="flex shrink-0 items-center justify-center rounded-md bg-indigo-100 p-1.5 text-indigo-600">
                                <Key size={18} />
                            </div>
                            <div className="flex min-w-0 flex-1 flex-col">
                                <div className="flex items-center gap-2">
                                    <span className="text-left text-sm font-bold leading-tight text-slate-800">
                                        {c.name}
                                    </span>
                                    {statusBadge(c.status)}
                                </div>
                                <span className="mt-0.5 break-all text-left font-mono text-[10px] leading-tight text-slate-500">
                                    {c.client_id}
                                </span>
                                {c.description && (
                                    <p className="mt-2 line-clamp-2 text-left text-[10px] leading-tight text-slate-600">
                                        {c.description}
                                    </p>
                                )}
                                {c.scopes.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {c.scopes.map((s) => (
                                            <code key={s} className="rounded bg-slate-100 px-1 py-0.5 text-[9px] text-slate-600">
                                                {s}
                                            </code>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-auto flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-[10px] text-slate-500">
                            <span className="capitalize">{c.environment}</span>
                            <span className="ml-auto flex items-center gap-1 text-slate-400">
                                <Clock size={10} />
                                {formatDate(c.created)}
                            </span>
                        </div>
                    </div>
                )}
            />

            <CreateOAuthClientDrawer
                isOpen={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={handleCreated}
                apiPath={apiBasePath}
                title="Create OAuth Client"
                subtitle={`Create an OAuth client for API access to ${appKey}.`}
            />

            <OAuthClientDetailDrawer
                isOpen={!!selectedClient}
                onClose={() => setSelectedClient(null)}
                onUpdated={handleUpdated}
                client={selectedClient}
                apiPath={apiBasePath}
            />
        </div>
    );
}
